import axios from "axios";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  PART_FACETS_QUERY_KEY,
  PARTS_SEARCH_QUERY_KEY,
  partsBase,
} from "./PartService";

// ===========================
// Bulk upload types
// ===========================

export interface PartSchemaUploadResponse {
  created_count: number;
  updated_count: number;
  error_count: number;
  errors: Array<{ part_type?: string; row?: number; error: string }>;
  schema_ids: number[];
}

export interface PartSchemaBatchDeleteResponse {
  deleted_count: number;
  error_count: number;
  errors: Array<{ schema_id: number; error: string }>;
  schema_ids: number[];
}

// ===========================
// Types
// ===========================

export type FeatureDataType = "string" | "integer" | "float" | "boolean";

export interface PartSchemaFeature {
  id: number;
  part_schema_fk: number;
  canonical_name: string;
  display_name?: string | null;
  data_type: FeatureDataType;
  unit?: string | null;
  position: number;
  required: boolean;
  aliases: string[];
}

export interface PartSchema {
  id: number;
  part_type: string;
  description?: string | null;
  parent_fk?: number | null;
  create_user_fk: number;
  create_date?: string | null;
  features: PartSchemaFeature[];
}

export interface PartSchemaFeatureValue {
  value: string;
  occurrence_count: number;
}

export interface PartSchemaFeatureCreate {
  canonical_name: string;
  display_name?: string | null;
  data_type?: FeatureDataType;
  unit?: string | null;
  position?: number;
  required?: boolean;
  aliases?: string[];
}

export interface PartSchemaCreate {
  part_type: string;
  description?: string | null;
  parent_fk?: number | null;
  features?: PartSchemaFeatureCreate[];
}

export interface PartSchemaUpdate {
  description?: string | null;
  parent_fk?: number | null;
}

export interface PartSchemaFeatureUpdate {
  display_name?: string | null;
  data_type?: FeatureDataType;
  unit?: string | null;
  position?: number;
  required?: boolean;
  aliases?: string[];
}

/** A single entry in a batch feature update: target id + fields to change. */
export interface PartSchemaFeatureUpdateItem extends PartSchemaFeatureUpdate {
  feature_id: number;
}

export interface ApplySchemaResponse {
  queued: number;
}

// ===========================
// Query Keys
// ===========================

export const PART_SCHEMAS_QUERY_KEY = "parts-ai.partSchemas";
export const PART_SCHEMA_DETAIL_QUERY_KEY = "parts-ai.partSchemaDetail";
export const PART_SCHEMA_EFFECTIVE_FEATURES_KEY =
  "parts-ai.partSchemaEffectiveFeatures";
export const PART_SCHEMA_FEATURE_VALUES_KEY =
  "parts-ai.partSchemaFeatureValues";

// ===========================
// API Functions
// ===========================

const listPartSchemas = async (): Promise<PartSchema[]> => {
  const response = await axios.get<PartSchema[]>(`${partsBase()}/part-schemas`);
  return response.data;
};

const getPartSchema = async (schemaId: number): Promise<PartSchema> => {
  const response = await axios.get<PartSchema>(
    `${partsBase()}/part-schemas/${schemaId}`,
  );
  return response.data;
};

const getEffectiveFeatures = async (
  schemaId: number,
): Promise<PartSchemaFeature[]> => {
  const response = await axios.get<PartSchemaFeature[]>(
    `${partsBase()}/part-schemas/${schemaId}/effective-features`,
  );
  return response.data;
};

const listFeatureValues = async (
  schemaId: number,
  featureId: number,
  limit = 500,
): Promise<PartSchemaFeatureValue[]> => {
  const response = await axios.get<PartSchemaFeatureValue[]>(
    `${partsBase()}/part-schemas/${schemaId}/features/${featureId}/values`,
    { params: { limit } },
  );
  return response.data;
};

const createPartSchema = async (
  payload: PartSchemaCreate,
): Promise<PartSchema> => {
  const response = await axios.post<PartSchema>(
    `${partsBase()}/part-schemas`,
    payload,
  );
  return response.data;
};

const updatePartSchema = async (
  schemaId: number,
  payload: PartSchemaUpdate,
): Promise<PartSchema> => {
  const response = await axios.patch<PartSchema>(
    `${partsBase()}/part-schemas/${schemaId}`,
    payload,
  );
  return response.data;
};

const deletePartSchema = async (schemaId: number): Promise<void> => {
  await axios.delete(`${partsBase()}/part-schemas/${schemaId}`);
};

const bulkDeletePartSchemas = async (
  schemaIds: number[],
): Promise<PartSchemaBatchDeleteResponse> => {
  const response = await axios.post<PartSchemaBatchDeleteResponse>(
    `${partsBase()}/part-schemas/batch-delete`,
    { schema_ids: schemaIds },
  );
  return response.data;
};

// Feature mutations are batch endpoints — callers that need to change a single
// feature pass a one-element list. The whole batch is validated server-side
// before anything commits.

const addFeatures = async (
  schemaId: number,
  features: PartSchemaFeatureCreate[],
): Promise<PartSchemaFeature[]> => {
  const response = await axios.post<PartSchemaFeature[]>(
    `${partsBase()}/part-schemas/${schemaId}/features`,
    { features },
  );
  return response.data;
};

const updateFeatures = async (
  schemaId: number,
  updates: PartSchemaFeatureUpdateItem[],
): Promise<PartSchemaFeature[]> => {
  const response = await axios.patch<PartSchemaFeature[]>(
    `${partsBase()}/part-schemas/${schemaId}/features`,
    { updates },
  );
  return response.data;
};

const removeFeatures = async (
  schemaId: number,
  featureIds: number[],
): Promise<void> => {
  await axios.post(
    `${partsBase()}/part-schemas/${schemaId}/features/batch-delete`,
    { feature_ids: featureIds },
  );
};

const applySchemaToParts = async (
  schemaId: number,
  useLlm = false,
): Promise<ApplySchemaResponse> => {
  const response = await axios.post<ApplySchemaResponse>(
    `${partsBase()}/part-schemas/${schemaId}/apply`,
    null,
    { params: useLlm ? { use_llm: true } : {} },
  );
  return response.data;
};

const uploadPartSchemas = async (
  file: File,
): Promise<PartSchemaUploadResponse> => {
  const formData = new FormData();
  formData.append("file", file);
  const response = await axios.post<PartSchemaUploadResponse>(
    `${partsBase()}/part-schemas/upload`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return response.data;
};

// ===========================
// React Query Hooks
// ===========================

export const useListPartSchemas = () => {
  return useQuery([PART_SCHEMAS_QUERY_KEY], () => listPartSchemas(), {
    refetchOnWindowFocus: false,
    staleTime: 60 * 1000,
  });
};

export const useGetPartSchema = (schemaId: number | undefined) => {
  return useQuery(
    [PART_SCHEMA_DETAIL_QUERY_KEY, schemaId],
    () => getPartSchema(schemaId!),
    { enabled: !!schemaId, refetchOnWindowFocus: false },
  );
};

export const useGetEffectiveFeatures = (schemaId: number | undefined) => {
  return useQuery(
    [PART_SCHEMA_EFFECTIVE_FEATURES_KEY, schemaId],
    () => getEffectiveFeatures(schemaId!),
    { enabled: !!schemaId, refetchOnWindowFocus: false, staleTime: 60 * 1000 },
  );
};

export const useListFeatureValues = (
  schemaId: number | undefined,
  featureId: number | undefined,
  options: { enabled?: boolean } = {},
) => {
  return useQuery(
    [PART_SCHEMA_FEATURE_VALUES_KEY, schemaId, featureId],
    () => listFeatureValues(schemaId!, featureId!),
    {
      enabled: (options.enabled ?? true) && !!schemaId && !!featureId,
      refetchOnWindowFocus: false,
    },
  );
};

const useInvalidateSchemas = () => {
  const queryClient = useQueryClient();
  return (schemaId?: number) => {
    queryClient.invalidateQueries([PART_SCHEMAS_QUERY_KEY]);
    if (schemaId !== undefined) {
      queryClient.invalidateQueries([PART_SCHEMA_DETAIL_QUERY_KEY, schemaId]);
      queryClient.invalidateQueries([
        PART_SCHEMA_EFFECTIVE_FEATURES_KEY,
        schemaId,
      ]);
    }
  };
};

export const useCreatePartSchema = () => {
  const invalidate = useInvalidateSchemas();
  return useMutation((payload: PartSchemaCreate) => createPartSchema(payload), {
    onSuccess: () => invalidate(),
  });
};

export const useUpdatePartSchema = () => {
  const invalidate = useInvalidateSchemas();
  return useMutation(
    ({ schemaId, payload }: { schemaId: number; payload: PartSchemaUpdate }) =>
      updatePartSchema(schemaId, payload),
    { onSuccess: (_d, v) => invalidate(v.schemaId) },
  );
};

export const useDeletePartSchema = () => {
  const invalidate = useInvalidateSchemas();
  return useMutation((schemaId: number) => deletePartSchema(schemaId), {
    onSuccess: () => invalidate(),
  });
};

export const useBulkDeletePartSchemas = () => {
  const invalidate = useInvalidateSchemas();
  return useMutation(
    (schemaIds: number[]) => bulkDeletePartSchemas(schemaIds),
    { onSuccess: () => invalidate() },
  );
};

export const useAddFeatures = () => {
  const invalidate = useInvalidateSchemas();
  return useMutation(
    ({
      schemaId,
      features,
    }: {
      schemaId: number;
      features: PartSchemaFeatureCreate[];
    }) => addFeatures(schemaId, features),
    { onSuccess: (_d, v) => invalidate(v.schemaId) },
  );
};

export const useUpdateFeatures = () => {
  const invalidate = useInvalidateSchemas();
  return useMutation(
    ({
      schemaId,
      updates,
    }: {
      schemaId: number;
      updates: PartSchemaFeatureUpdateItem[];
    }) => updateFeatures(schemaId, updates),
    { onSuccess: (_d, v) => invalidate(v.schemaId) },
  );
};

export const useRemoveFeatures = () => {
  const invalidate = useInvalidateSchemas();
  return useMutation(
    ({ schemaId, featureIds }: { schemaId: number; featureIds: number[] }) =>
      removeFeatures(schemaId, featureIds),
    { onSuccess: (_d, v) => invalidate(v.schemaId) },
  );
};

/**
 * Back-populate a schema onto existing parts of its type (deterministic).
 * Invalidates parts search/facets so the UI reflects the canonicalized parts.
 */
export const useApplySchemaToParts = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ schemaId, useLlm }: { schemaId: number; useLlm?: boolean }) =>
      applySchemaToParts(schemaId, useLlm ?? false),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([PARTS_SEARCH_QUERY_KEY]);
        queryClient.invalidateQueries([PART_FACETS_QUERY_KEY]);
      },
    },
  );
};

/**
 * Bulk create/update part schemas from an Excel/CSV/JSON file. Invalidates the
 * schema list + effective-features so the editor reflects the imported schemas.
 */
export const useUploadPartSchemas = () => {
  const queryClient = useQueryClient();
  return useMutation((file: File) => uploadPartSchemas(file), {
    onSuccess: () => {
      queryClient.invalidateQueries([PART_SCHEMAS_QUERY_KEY]);
      queryClient.invalidateQueries([PART_SCHEMA_EFFECTIVE_FEATURES_KEY]);
    },
  });
};
